import { addHelpButton } from './help.js';
import { mk, initWidget, shuffle, renderMath, DEFAULT_MATH_DELIMITERS } from './utils.js';

const HELP_TEXT = {
  en: 'Read the front of each card, then click Flip to see the answer. Rate how well you knew it: Got it (you knew it), Almost (close but not quite), or No (didn\'t know it). Cards rated Almost or No will reappear until you get them right.',
  fr: 'Lisez le recto de la carte, puis cliquez sur Retourner pour voir la réponse. Évaluez : Compris (vous le saviez), Presque (proche) ou Non (vous ne le saviez pas). Les cartes notées Presque ou Non réapparaîtront.',
  es: 'Lea el frente de la tarjeta y haga clic en Voltear para ver la respuesta. Califique: Entendido (lo sabía), Casi (cerca) o No (no lo sabía). Las tarjetas con Casi o No reaparecerán hasta que las domine.',
};

function render({ model, el }) {
  const container = initWidget(el, model.get('question'));

  const cards = model.get('cards');
  const total = cards.length;
  const done = new Set();
  const results = {};

  // Build initial queue (0..n-1), shuffle if requested
  let queue = cards.map((_, i) => i);
  if (model.get('shuffle')) shuffle(queue);

  // Progress bar
  const progressWrap = mk('div', 'forma-progress');
  const progressFill = mk('div', 'forma-progress-fill'); progressFill.style.width = '0%';
  progressWrap.appendChild(progressFill);
  container.appendChild(progressWrap);

  const counterEl = mk('div', 'forma-instructions');
  container.appendChild(counterEl);

  // Card face — cardContent is a span inside the flex container so that
  // inline text and KaTeX spans are siblings within one flex item, preventing
  // flex layout from collapsing the whitespace between them.
  const cardEl = mk('div', 'forma-card forma-card-front');
  const cardContent = mk('span');
  cardEl.appendChild(cardContent);
  container.appendChild(cardEl);

  // Flip button
  const flipBtn = mk('button', 'forma-btn forma-btn-secondary', 'Flip');
  flipBtn.style.marginBottom = '12px';
  container.appendChild(flipBtn);

  // Rating buttons (hidden until flipped)
  const ratingRow = mk('div', 'forma-rating-btns'); ratingRow.style.display = 'none';
  const gotItBtn  = mk('button', 'forma-btn forma-btn-primary', '✓ Got it');
  const almostBtn = mk('button', 'forma-btn forma-btn-secondary', '~ Almost');
  const noBtn     = mk('button', 'forma-btn forma-btn-danger', '✗ No');
  ratingRow.append(gotItBtn, almostBtn, noBtn);
  container.appendChild(ratingRow);

  function updateCounter() {
    if (queue.length === 0) return;
    counterEl.textContent = `Card ${done.size + 1} of ${total} — ${queue.length} remaining in queue`;
  }

  function showCard() {
    if (queue.length === 0) return;
    const idx = queue[0];
    cardContent.textContent = cards[idx].front;
    renderMath(cardContent, model.get('math_delimiters'));
    cardEl.className = 'forma-card forma-card-front';
    flipBtn.style.display = '';
    ratingRow.style.display = 'none';
    updateCounter();
  }

  function showComplete() {
    cardContent.textContent = '🎉 All cards reviewed!';
    cardEl.className = 'forma-card forma-card-back';
    counterEl.textContent = `Completed ${total} card${total !== 1 ? 's' : ''}.`;
    flipBtn.style.display = 'none';
    ratingRow.style.display = 'none';
    progressFill.style.width = '100%';
    const restartBtn = mk('button', 'forma-btn forma-btn-secondary', 'Restart');
    restartBtn.addEventListener('click', () => {
      done.clear();
      queue = cards.map((_, i) => i);
      if (model.get('shuffle')) shuffle(queue);
      restartBtn.remove();
      showCard();
    });
    container.appendChild(restartBtn);
  }

  flipBtn.addEventListener('click', () => {
    const idx = queue[0];
    cardContent.textContent = cards[idx].back;
    renderMath(cardContent, model.get('math_delimiters'));
    cardEl.className = 'forma-card forma-card-back';
    flipBtn.style.display = 'none';
    ratingRow.style.display = 'flex';
  });

  function rate(rating) {
    const idx = queue.shift();
    if (!results[idx]) results[idx] = { attempts: 0 };
    results[idx].rating = rating;
    results[idx].attempts++;
    if (rating === 'got_it') {
      done.add(idx);
    } else if (rating === 'almost') {
      queue.splice(Math.min(2, queue.length), 0, idx);
    } else {
      queue.splice(Math.min(1, queue.length), 0, idx);
    }
    progressFill.style.width = `${Math.round(done.size / total * 100)}%`;
    model.set('value', { results, complete: done.size === total });
    model.save_changes();
    if (done.size === total) showComplete(); else showCard();
  }

  gotItBtn.addEventListener('click', () => rate('got_it'));
  almostBtn.addEventListener('click', () => rate('almost'));
  noBtn.addEventListener('click', () => rate('no'));

  showCard();
  addHelpButton(container, model.get('lang'), HELP_TEXT);
  el.appendChild(container);
}

// Parse a <div class="forma-flashcard"> block.
// Optional heading comes from the first <p>; cards from <dt>/<dd> pairs in a <dl>.
export function parseHTML(div) {
  const question = div.querySelector('p')?.textContent.trim() ?? '';
  const cards = [...div.querySelectorAll('dt')].map(dt => ({
    front: dt.textContent.trim(),
    back:  dt.nextElementSibling?.textContent.trim() ?? '',
  }));
  const raw = div.dataset.mathDelimiters;
  const math_delimiters = raw ? JSON.parse(raw) : DEFAULT_MATH_DELIMITERS;
  return { question, cards, shuffle: true, lang: div.dataset.lang ?? 'en', math_delimiters };
}

export default { render };
